import { useState, useEffect } from 'react'
import { useDistributePoints, useTeacherStats } from '@/lib/teacher-queries'
import type { StudentBasicInfo } from '@/lib/teacher-queries'
import { getFileUrl } from '@/lib/pocketbase'

interface DistributePointsModalProps {
  student: StudentBasicInfo | null
  isOpen: boolean
  onClose: () => void
}

const DistributePointsModal = ({ student, isOpen, onClose }: DistributePointsModalProps) => {
  const [amount, setAmount] = useState<string>('')
  const [error, setError] = useState<string>('')

  const { data: stats } = useTeacherStats()
  const distributePoints = useDistributePoints()

  // Reset form when modal opens with new student
  useEffect(() => {
    if (isOpen && student) {
      setAmount('')
      setError('')
    }
  }, [isOpen, student])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!student) return

    const numAmount = parseFloat(amount)

    // Validation
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount greater than 0')
      return
    }

    if (stats && numAmount > stats.points_to_distribute) {
      setError(
        `You only have ${stats.points_to_distribute.toFixed(0)} points available to distribute`
      )
      return
    }

    try {
      await distributePoints.mutateAsync({
        student_id: student.id,
        amount: numAmount,
      })

      // Success - close modal
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to distribute points')
    }
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string or valid numbers
    if (value === '' || !isNaN(parseFloat(value))) {
      setAmount(value)
      setError('')
    }
  }

  if (!student) return null

  const avatarUrl = student.avatar ? getFileUrl('users', student.id, student.avatar) : null
  const maxPoints = stats?.points_to_distribute || 0

  return (
    <>
      <input
        type="checkbox"
        id="distribute-points-modal"
        className="modal-toggle"
        checked={isOpen}
        readOnly
      />
      <div className="modal">
        <div className="modal-box bg-white">
          <h3 className="font-bold text-lg text-primary mb-4">Distribute Points</h3>

          {/* Student Info */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-base-100 rounded-lg">
            <div className="avatar">
              <div className="w-16 h-16 rounded-full">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={student.name || student.username} />
                ) : (
                  <div className="bg-primary/10 w-full h-full flex items-center justify-center text-primary font-bold text-xl">
                    {(student.name || student.username || '?')[0].toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="font-bold text-lg">{student.name || student.username}</div>
              <div className="text-sm text-base-content/60">@{student.username}</div>
              <div className="text-sm mt-1">
                Current Balance:{' '}
                <span className="font-semibold text-primary">
                  {student.points_balance?.toFixed(0) || 0} pts
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">Points to Give</span>
                <span className="label-text-alt">
                  Available: {maxPoints.toFixed(0)} pts
                </span>
              </label>
              <input
                type="number"
                placeholder="Enter amount"
                className="input input-bordered w-full"
                value={amount}
                onChange={handleAmountChange}
                min="0"
                step="1"
                max={maxPoints}
                required
                autoFocus
              />
              {error && (
                <label className="label">
                  <span className="label-text-alt text-error">{error}</span>
                </label>
              )}
            </div>

            {/* Quick amount buttons */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setAmount('10')}
                disabled={maxPoints < 10}
              >
                +10
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setAmount('50')}
                disabled={maxPoints < 50}
              >
                +50
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setAmount('100')}
                disabled={maxPoints < 100}
              >
                +100
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setAmount(maxPoints.toString())}
                disabled={maxPoints <= 0}
              >
                Max
              </button>
            </div>

            {/* Action buttons */}
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={distributePoints.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={distributePoints.isPending || !amount || parseFloat(amount) <= 0}
              >
                {distributePoints.isPending ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Distributing...
                  </>
                ) : (
                  'Distribute Points'
                )}
              </button>
            </div>
          </form>

          {distributePoints.isSuccess && (
            <div className="alert alert-success mt-4">
              <span>Points distributed successfully!</span>
            </div>
          )}
        </div>
        <label className="modal-backdrop" htmlFor="distribute-points-modal" onClick={onClose}>
          Close
        </label>
      </div>
    </>
  )
}

export default DistributePointsModal
